import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";

import AppHeader from "./AppHeader";
import MobileHistoryPanel from "./MobileHistoryPanel";
import { questionPool, results } from "./HiddenQuestionsData";
import styles from "./HiddenGenius.module.css";

export default function HiddenGenius(props) {
  const {
    currentUser,
    setShowHistoryMobile,
    showFeedback,
    feedbackText,
    setFeedbackText,
    feedbackType,
    setFeedbackType,
    setShowFeedback,
    handleSubmitFeedback,
    showFeedbackSuccess,
    onHomeClick,
    showHistoryMobile: mobileHistoryState,
    sessions,
    currentSessionId,
    confirmingDelete,
    setConfirmingDelete,
    openSession,
    deleteSession,
    groupSessionsByDate,
    lastUserQuestionSafe,
    getSubjectIcon,
    showSocialMenu,
    setShowSocialMenu,
    socialMenuRef,
    newSessions,
    openDeleteAllModal,
    confirmDeleteAll,
    closeDeleteAllModal,
    DangerModal,
  } = props;

  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("profile");
  const [profileSubTab, setProfileSubTab] = useState("overview");

  const [step, setStep] = useState("upload");
  const [photo, setPhoto] = useState(null);
  const [index, setIndex] = useState(0);
  const [scores, setScores] = useState({});
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [challengeCopied, setChallengeCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Precision metrics tracking
  const [questionTimings, setQuestionTimings] = useState([]); // Seconds taken per answered question
  const [qStartTime, setQStartTime] = useState(null);

  const cardRef = useRef(null);

  const TOTAL_TIME = 30;
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const timerRef = useRef(null);

  const [activeQuestions, setActiveQuestions] = useState([]);

  const pick3RandomQuestions = () => {
    const seenIds = JSON.parse(localStorage.getItem("agni_seen_questions") || "[]");
    let available = questionPool.filter((q) => !seenIds.includes(q.id));

    if (available.length < 3) {
      localStorage.removeItem("agni_seen_questions");
      available = [...questionPool];
    }

    const shuffled = [...available].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, 3);

    const newSeenIds = [...seenIds, ...selected.map((q) => q.id)];
    localStorage.setItem("agni_seen_questions", JSON.stringify(newSeenIds));

    setActiveQuestions(selected);
  };

  useEffect(() => {
    pick3RandomQuestions();
  }, []);

  const calculateResultAndFinish = (finalScores, timings = questionTimings) => {
    clearInterval(timerRef.current);
    const answeredCount = timings.length;

    // Check if the user failed to answer all questions before time ran out
    if (answeredCount < activeQuestions.length) {
      setResult({
        id: "timeout",
        title: "Incomplete Assessment",
        subtitle: "TIME EXPIRED",
        emoji: "⏰",
        isTimeout: true,
        answeredCount,
      });
      setStep("result");
      return;
    }

    const keys = Object.keys(finalScores);
    const winner =
      keys.length > 0
        ? keys.reduce((a, b) => (finalScores[a] > finalScores[b] ? a : b))
        : "inventor";

    setResult({
      ...(results[winner] || results["inventor"]),
      isTimeout: false,
      answeredCount,
    });
    setStep("result");
  };

  useEffect(() => {
    if (step === "questions") {
      setTimeLeft(TOTAL_TIME);
      setQuestionTimings([]);
      setQStartTime(Date.now());

      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setScores((currentScores) => {
              setQuestionTimings((currentTimings) => {
                calculateResultAndFinish(currentScores, currentTimings);
                return currentTimings;
              });
              return currentScores;
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timerRef.current);
  }, [step]);

  const handleToggleMobileMenu = () => {
    if (typeof setShowHistoryMobile === "function") {
      setShowHistoryMobile((prev) => !prev);
    }
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPhoto(URL.createObjectURL(file));
    setStep("analyzing");

    setTimeout(() => {
      setStep("questions");
    }, 2200);
  };

  const handleAnswer = (type) => {
    const now = Date.now();
    const timeSpentOnThisQ = Math.max(0.4, (now - qStartTime) / 1000);
    const updatedTimings = [...questionTimings, timeSpentOnThisQ];
    setQuestionTimings(updatedTimings);

    const updatedScores = {
      ...scores,
      [type]: (scores[type] || 0) + 1,
    };
    setScores(updatedScores);

    if (index + 1 < activeQuestions.length) {
      setIndex(index + 1);
      setQStartTime(Date.now());
    } else {
      calculateResultAndFinish(updatedScores, updatedTimings);
    }
  };

  const handleChallengeFriends = async () => {
    if (!result) return;
    const challengeText = `🔥 I unlocked "${result.title}" ${result.emoji} in 30s! Think you can beat my AI Mindset score? Take the challenge now:`;
    const shareData = {
      title: `⚡ AGNI AI Speed Challenge`,
      text: challengeText,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
      setChallengeCopied(true);
      setTimeout(() => setChallengeCopied(false), 2500);
    }
  };

  const handleShare = async () => {
    if (!result) return;
    const shareData = {
      title: `AGNI AI: I unlocked ${result.title}!`,
      text: `I took the Hidden Genius challenge and unlocked "${result.title}" ${result.emoji}! Check out your personality on AGNI AI:`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleDownloadCard = async () => {
    // Safely extract variables using component state & result props
    const avatarUrl = photo || result?.avatarUrl || null;

    // Real metric calculations matching your current UI
    const totalSecondsSpent = questionTimings.length > 0 ? questionTimings.reduce((acc, t) => acc + t, 0) : 1.8;
    const avgSecondsPerQ = questionTimings.length > 0 ? totalSecondsSpent / questionTimings.length : 0.6;
    const dynamicSpeed = Math.min(99, Math.max(50, Math.round(100 - (avgSecondsPerQ * 5))));
    const maxChoiceScore = Object.keys(scores).length > 0 ? Math.max(...Object.values(scores)) : 2;
    const dynamicFocus = activeQuestions.length > 0 ? Math.round((maxChoiceScore / activeQuestions.length) * 100) : 67;
    const dynamicAgility = Math.round((dynamicSpeed * 0.5) + (dynamicFocus * 0.5));
    const calculatedLevel = Math.round((dynamicSpeed * 0.4) + (dynamicFocus * 0.4) + (dynamicAgility * 0.2));

    try {
      setDownloading(true);

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const width = 900;
      const height = 1100;
      canvas.width = width;
      canvas.height = height;

      // Background: Deep Space Cyber Gradient
      const bgGradient = ctx.createLinearGradient(0, 0, width, height);
      bgGradient.addColorStop(0, "#070a12");
      bgGradient.addColorStop(0.5, "#0f172a");
      bgGradient.addColorStop(1, "#1e1b4b");
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);

      // Neon Radial Glow
      const radialGlow = ctx.createRadialGradient(
        width / 2, 350, 50,
        width / 2, 350, 450
      );
      radialGlow.addColorStop(0, "rgba(56, 189, 248, 0.25)");
      radialGlow.addColorStop(0.5, "rgba(99, 102, 241, 0.12)");
      radialGlow.addColorStop(1, "rgba(7, 10, 18, 0)");
      ctx.fillStyle = radialGlow;
      ctx.fillRect(0, 0, width, height);

      // Glowing Border Box
      ctx.lineWidth = 6;
      const borderGradient = ctx.createLinearGradient(0, 0, width, height);
      borderGradient.addColorStop(0, "#38bdf8");
      borderGradient.addColorStop(0.5, "#818cf8");
      borderGradient.addColorStop(1, "#c084fc");
      ctx.strokeStyle = borderGradient;
      ctx.shadowColor = "#38bdf8";
      ctx.shadowBlur = 25;
      ctx.strokeRect(30, 30, width - 60, height - 60);
      ctx.shadowBlur = 0;

      // Header Top Row: "AGNITUTOR • AI MINDSET" (left)
      ctx.textAlign = "left";
      ctx.fillStyle = "#38bdf8";
      ctx.font = "bold 24px Inter, sans-serif";
      ctx.fillText("AGNITUTOR", 90, 110);
      ctx.fillStyle = "#64748b";
      ctx.fillText("•", 235, 110);
      ctx.fillStyle = "#94a3b8";
      ctx.fillText("AI MINDSET", 260, 110);

      // Level Badge on Right (e.g. LVL 82)
      const lvlText = `LVL ${calculatedLevel}`;
      ctx.font = "bold 22px Inter, sans-serif";
      const lvlBadgeWidth = 110;
      const lvlBadgeHeight = 46;
      const lvlBadgeX = width - 90 - lvlBadgeWidth;
      const lvlBadgeY = 82;

      ctx.fillStyle = "#8b5cf6";
      ctx.beginPath();
      ctx.roundRect(lvlBadgeX, lvlBadgeY, lvlBadgeWidth, lvlBadgeHeight, 14);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.fillText(lvlText, lvlBadgeX + lvlBadgeWidth / 2, lvlBadgeY + 30);

      // User Avatar Rendering
      const userImg = new Image();
      userImg.crossOrigin = "anonymous";
      if (avatarUrl) {
        userImg.src = avatarUrl;
      }

      await new Promise((resolve) => {
        userImg.onload = resolve;
        userImg.onerror = resolve;
      });

      const avatarRadius = 75;
      const avatarX = width / 2;
      const avatarY = 240;

      // Outer Glow Ring for Avatar
      ctx.save();
      ctx.beginPath();
      ctx.arc(avatarX, avatarY, avatarRadius + 8, 0, Math.PI * 2);
      ctx.fillStyle = "#a855f7";
      ctx.shadowColor = "#a855f7";
      ctx.shadowBlur = 20;
      ctx.fill();
      ctx.restore();

      // Draw Avatar Image or Fallback
      if (userImg.complete && userImg.naturalWidth !== 0 && avatarUrl) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(avatarX, avatarY, avatarRadius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(
          userImg,
          avatarX - avatarRadius,
          avatarY - avatarRadius,
          avatarRadius * 2,
          avatarRadius * 2
        );
        ctx.restore();
      } else {
        ctx.fillStyle = "#1e293b";
        ctx.beginPath();
        ctx.arc(avatarX, avatarY, avatarRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 55px Inter, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("👤", avatarX, avatarY + 18);
      }

      // Small Badge Overlay on Avatar (e.g. 🚀)
      ctx.save();
      ctx.beginPath();
      ctx.arc(avatarX + 50, avatarY + 50, 24, 0, Math.PI * 2);
      ctx.fillStyle = "#0f172a";
      ctx.strokeStyle = "#38bdf8";
      ctx.lineWidth = 3;
      ctx.fill();
      ctx.stroke();
      ctx.font = "22px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(result?.emoji || "🚀", avatarX + 50, avatarY + 58);
      ctx.restore();

      // Subtitle Tag (e.g. FEARLESS FRONTIER PIONEER)
      ctx.textAlign = "center";
      ctx.fillStyle = "#a855f7";
      ctx.font = "bold 22px Inter, sans-serif";
      ctx.fillText((result?.subtitle || "FEARLESS FRONTIER PIONEER").toUpperCase(), width / 2, 360);

      // Result Title (e.g. Space Explorer Mindset)
      const titleText = result?.title || "Space Explorer Mindset";
      ctx.fillStyle = "#ffffff";
      ctx.font = "900 52px Inter, sans-serif";
      ctx.fillText(titleText, width / 2, 430);

      // Skill Meters Section
      const boxX = 90;
      const boxW = width - 180;
      let currentY = 480;

      const renderSkillBar = (label, percentage, color, yPos) => {
        ctx.textAlign = "left";
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 20px Inter, sans-serif";
        ctx.fillText(label, boxX + 20, yPos);

        ctx.textAlign = "right";
        ctx.font = "bold 22px Inter, sans-serif";
        ctx.fillText(`${percentage}%`, boxX + boxW - 20, yPos);

        // Track
        const trackY = yPos + 14;
        const trackH = 14;
        ctx.fillStyle = "#1e293b";
        ctx.beginPath();
        ctx.roundRect(boxX + 20, trackY, boxW - 40, trackH, 7);
        ctx.fill();

        // Fill
        const fillW = Math.max(20, ((boxW - 40) * percentage) / 100);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(boxX + 20, trackY, fillW, trackH, 7);
        ctx.fill();
      };

      renderSkillBar(`⚡ SPEED (${avgSecondsPerQ.toFixed(1)}s avg/question)`, dynamicSpeed, "#38bdf8", currentY);
      renderSkillBar(`🎯 FOCUS (${dynamicFocus}% Match Precision)`, dynamicFocus, "#a855f7", currentY + 80);
      renderSkillBar(`🧠 AGILITY (Adaptive Execution)`, dynamicAgility, "#34d399", currentY + 160);

      // AI Performance Analysis Report Box
      const reportY = currentY + 250;
      const reportH = 120;
      ctx.fillStyle = "#0f172a";
      ctx.strokeStyle = "#334155";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(boxX, reportY, boxW, reportH, 16);
      ctx.fill();
      ctx.stroke();

      ctx.textAlign = "left";
      ctx.fillStyle = "#38bdf8";
      ctx.font = "bold 18px Inter, sans-serif";
      ctx.fillText("📊 AI PERFORMANCE ANALYSIS", boxX + 30, reportY + 38);

      ctx.fillStyle = "#94a3b8";
      ctx.font = "500 18px Inter, sans-serif";
      ctx.fillText(
        `Completed in ${totalSecondsSpent.toFixed(1)}s total (${avgSecondsPerQ.toFixed(1)}s/Q) with ${dynamicFocus}% profile consistency.`,
        boxX + 30,
        reportY + 74
      );
      ctx.fillText(
        `Rapid-fire cognitive processing with zero hesitation detected.`,
        boxX + 30,
        reportY + 100
      );

      // Call To Action Box
      const ctaY = reportY + reportH + 30;
      const ctaH = 130;
      
      const ctaGrad = ctx.createLinearGradient(boxX, ctaY, boxX + boxW, ctaY);
      ctaGrad.addColorStop(0, "#1e1b4b");
      ctaGrad.addColorStop(1, "#312e81");

      ctx.fillStyle = ctaGrad;
      ctx.strokeStyle = "#6366f1";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(boxX, ctaY, boxW, ctaH, 16);
      ctx.fill();
      ctx.stroke();

      ctx.textAlign = "center";
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 24px Inter, sans-serif";
      ctx.fillText("CAN YOU BEAT THIS SCORE?", width / 2, ctaY + 45);

      // Button Pill Inside CTA
      const btnW = 300;
      const btnH = 50;
      const btnX = width / 2 - btnW / 2;
      const btnY = ctaY + 65;

      const btnGrad = ctx.createLinearGradient(btnX, btnY, btnX + btnW, btnY);
      btnGrad.addColorStop(0, "#38bdf8");
      btnGrad.addColorStop(1, "#6366f1");

      ctx.fillStyle = btnGrad;
      ctx.beginPath();
      ctx.roundRect(btnX, btnY, btnW, btnH, 25);
      ctx.fill();

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 20px Inter, sans-serif";
      ctx.fillText("AgniTutor.com ➔", width / 2, btnY + 32);

      // Trigger Download
      const imageURI = canvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.download = `AGNI_Hidden_Genius_${(result?.title || "Result").replace(/\s+/g, "_")}.png`;
      link.href = imageURI;
      link.click();
    } catch (error) {
      console.error("Card generation failed:", error);
    } finally {
      setDownloading(false);
    }
  };

  const restart = () => {
    clearInterval(timerRef.current);
    pick3RandomQuestions();
    setStep("upload");
    setPhoto(null);
    setIndex(0);
    setScores({});
    setResult(null);
    setQuestionTimings([]);
    setQStartTime(null);
    setCopied(false);
    setChallengeCopied(false);
    setTimeLeft(TOTAL_TIME);
  };

  return (
    <div className={styles.page}>
      <div className={styles.whiteHeaderWrapper}>
        <AppHeader
          setShowHistoryMobile={handleToggleMobileMenu}
          showFeedback={showFeedback}
          feedbackText={feedbackText}
          setFeedbackText={setFeedbackText}
          feedbackType={feedbackType}
          setFeedbackType={setFeedbackType}
          setShowFeedback={setShowFeedback}
          handleSubmitFeedback={handleSubmitFeedback}
          showFeedbackSuccess={showFeedbackSuccess}
          onHomeClick={onHomeClick || (() => navigate("/"))}
          showSocialMenu={showSocialMenu}
          setShowSocialMenu={setShowSocialMenu}
          socialMenuRef={socialMenuRef}
        />
      </div>

      <main className={styles.mainContent}>
        {/* Step 1: Upload */}
        {step === "upload" && (
          <div className={styles.card}>
            <div className={styles.heroGlow}></div>
            <div className={styles.iconHero}>🧠</div>
            <h1 className={styles.title}>Discover Your Hidden Genius</h1>

            <p className={styles.subtitle}>
              Take the <strong>30s Speed Challenge</strong> to unveil your AI mindset & flex on your friends!
            </p>

            <label className={styles.uploadBtn}>
              <span className={styles.btnIcon}>📸</span> Upload Photo & Start
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handlePhoto}
              />
            </label>
          </div>
        )}

        {/* Step 2: Analyzing */}
        {step === "analyzing" && (
          <div className={styles.card}>
            <div className={styles.avatarScanWrapper}>
              {photo && <img src={photo} alt="User Preview" className={styles.scanImage} />}
              <div className={styles.scanBeam}></div>
              <div className={styles.pulseRing}></div>
            </div>

            <h2 className={styles.cardHeader}>⚡ Neural Assessment In Progress...</h2>

            <div className={styles.loaderBar}>
              <div className={styles.loaderFill}></div>
            </div>
          </div>
        )}

        {/* Step 3: Questions */}
        {step === "questions" && activeQuestions.length > 0 && (
          <div className={styles.card}>
            <div className={styles.quizHeader}>
              <div className={styles.quizMeta}>
                <span className={styles.stepBadge}>
                  {index + 1} / {activeQuestions.length}
                </span>
                <span className={`${styles.timerBadge} ${timeLeft <= 10 ? styles.timerWarning : ""}`}>
                  ⏱️ {timeLeft}s
                </span>
              </div>

              <div className={styles.timerTrack}>
                <div
                  className={styles.timerProgress}
                  style={{ width: `${(timeLeft / TOTAL_TIME) * 100}%` }}
                ></div>
              </div>
            </div>

            <h3 className={styles.questionText}>
              {activeQuestions[index].question}
            </h3>

            <div className={styles.optionWrap}>
              {activeQuestions[index].options.map((opt, i) => (
                <button
                  key={i}
                  className={styles.option}
                  onClick={() => handleAnswer(opt.type)}
                >
                  <span className={styles.optionIndex}>{String.fromCharCode(65 + i)}</span>
                  <span className={styles.optionText}>{opt.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Final Result Screen */}
        {step === "result" && result && (() => {
          // Timeout layout: When user answered 0 or fewer than 3 questions
          if (result.isTimeout) {
            return (
              <div className={styles.resultContainer}>
                <div className={styles.resultCard} ref={cardRef} data-card="result-card">
                  <div className={styles.cardHeaderRow}>
                    <div className={styles.cardBrandHeader} data-card="brand-header">
                      <span className={styles.brandName}>AGNITUTOR</span>
                      <span className={styles.brandDot}>•</span>
                      <span className={styles.brandTagline}>AI MINDSET</span>
                    </div>
                    <div className={styles.levelBadge}>LVL --</div>
                  </div>

                  <div className={styles.photoResultContainer}>
                    {photo ? (
                      <img src={photo} alt="Result" className={styles.userResultPhoto} />
                    ) : (
                      <div className={styles.defaultAvatarPlaceholder}>👤</div>
                    )}
                    <div className={styles.emojiBadgeOverlay}>⏰</div>
                  </div>

                  <p className={styles.resultTag} data-card="result-tag">{result.subtitle}</p>
                  <h1 className={styles.title} data-card="title">{result.title}</h1>

                  <div className={styles.analysisReportBox}>
                    <div className={styles.analysisHeader}>⚠️ ASSESSMENT INCOMPLETE</div>
                    <p className={styles.analysisText}>
                      You answered <strong>{result.answeredCount} of {activeQuestions.length}</strong> questions before the 30-second timer expired. Speed and mindset analytics require completing all questions.
                    </p>
                  </div>

                  <div className={styles.ctaBanner} data-card="cta-banner">
                    <div className={styles.ctaContent}> 
                      <p className={styles.ctaText} data-card="cta-text">TRY AGAIN TO UNLOCK YOUR SCORE</p>
                    </div>
                    <a href="https://agnitutor.com" target="_blank" rel="noopener noreferrer" className={styles.domainBtn}>
                      AgniTutor.com ➔
                    </a>
                  </div>
                </div>

                <div className={styles.buttonGroup}> 
                  <button className={styles.restartBtn} onClick={restart}>
                    🔄 Try Again
                  </button>
                </div>
              </div>
            );
          }

          // Complete layout: Real mathematical metrics calculated from timing array
          const totalSecondsSpent = questionTimings.reduce((acc, t) => acc + t, 0);
          const avgSecondsPerQ = totalSecondsSpent / questionTimings.length;

          const dynamicSpeed = Math.min(99, Math.max(50, Math.round(100 - (avgSecondsPerQ * 5))));
          const maxChoiceScore = Math.max(...Object.values(scores));
          const dynamicFocus = Math.round((maxChoiceScore / activeQuestions.length) * 100);
          const dynamicAgility = Math.round((dynamicSpeed * 0.5) + (dynamicFocus * 0.5));
          const calculatedLevel = Math.round((dynamicSpeed * 0.4) + (dynamicFocus * 0.4) + (dynamicAgility * 0.2));

          return (
            <div className={styles.resultContainer}>
              <div className={styles.resultCard} ref={cardRef} data-card="result-card">
                
                {/* Top Header & Dynamic Level Badge */}
                <div className={styles.cardHeaderRow}>
                  <div className={styles.cardBrandHeader} data-card="brand-header">
                    <span className={styles.brandName}>AGNITUTOR</span>
                    <span className={styles.brandDot}>•</span>
                    <span className={styles.brandTagline}>AI MINDSET</span>
                  </div>
                  <div className={styles.levelBadge}>LVL {calculatedLevel}</div>
                </div>

                {/* Profile Avatar */}
                <div className={styles.photoResultContainer}>
                  <div className={styles.avatarGlowRing}></div>
                  {photo ? (
                    <img src={photo} alt="Genius Result" className={styles.userResultPhoto} />
                  ) : (
                    <div className={styles.defaultAvatarPlaceholder}>👤</div>
                  )}
                  <div className={styles.emojiBadgeOverlay}>{result.emoji}</div>
                </div>

                <p className={styles.resultTag} data-card="result-tag">{result.subtitle}</p>
                <h1 className={styles.title} data-card="title">{result.title}</h1>

                {/* Real-Data Skill Bars */}
                <div className={styles.skillMeterContainer}>
                  <div className={styles.skillItem}>
                    <div className={styles.skillLabel}>
                      <span>⚡ SPEED ({avgSecondsPerQ.toFixed(1)}s avg/question)</span>
                      <strong>{dynamicSpeed}%</strong>
                    </div>
                    <div className={styles.skillTrack}>
                      <div 
                        className={styles.skillFill} 
                        style={{ width: `${dynamicSpeed}%`, background: "#38BDF8" }}
                      ></div>
                    </div>
                  </div>

                  <div className={styles.skillItem}>
                    <div className={styles.skillLabel}>
                      <span>🎯 FOCUS ({dynamicFocus}% Match Precision)</span>
                      <strong>{dynamicFocus}%</strong>
                    </div>
                    <div className={styles.skillTrack}>
                      <div 
                        className={styles.skillFill} 
                        style={{ width: `${dynamicFocus}%`, background: "#A855F7" }}
                      ></div>
                    </div>
                  </div>

                  <div className={styles.skillItem}>
                    <div className={styles.skillLabel}>
                      <span>🧠 AGILITY (Adaptive Execution)</span>
                      <strong>{dynamicAgility}%</strong>
                    </div>
                    <div className={styles.skillTrack}>
                      <div 
                        className={styles.skillFill} 
                        style={{ width: `${dynamicAgility}%`, background: "#34D399" }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Real Data AI Performance Analysis Report */}
                <div className={styles.analysisReportBox}>
                  <div className={styles.analysisHeader}>📊 AI PERFORMANCE ANALYSIS</div>
                  <p className={styles.analysisText}>
                    Completed in <strong>{totalSecondsSpent.toFixed(1)}s total</strong> ({avgSecondsPerQ.toFixed(1)}s/Q) with <strong>{dynamicFocus}% profile consistency</strong>. 
                    {avgSecondsPerQ < 2.5 
                      ? " Rapid-fire cognitive processing with zero hesitation detected." 
                      : " Strategic and deliberate choice pattern observed."}
                  </p>
                </div>

                {/* Challenge CTA Box */}
                <div className={styles.ctaBanner} data-card="cta-banner">
                  <div className={styles.ctaContent}> 
                    <p className={styles.ctaText} data-card="cta-text">
                      CAN YOU BEAT THIS SCORE?
                    </p>
                  </div>
                  <a 
                    href="https://agnitutor.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={styles.domainBtn}
                  >
                    AgniTutor.com ➔
                  </a>
                </div>

              </div>

              {/* Action Buttons */}
              <div className={styles.buttonGroup}> 
                <button className={styles.downloadBtn} onClick={handleDownloadCard} disabled={downloading}>
                  {downloading ? "⏳ Saving..." : "📥 Save Card"}
                </button>

                <button className={styles.shareBtn} onClick={handleShare}>
                  {copied ? "📋 Copied!" : "📤 Share"}
                </button>

                <button className={styles.restartBtn} onClick={restart}>
                  🔄 Retry
                </button>
              </div>
            </div>
          );
        })()}
      </main>

      <MobileHistoryPanel
        showHistoryMobile={mobileHistoryState}
        sessions={sessions}
        currentSessionId={currentSessionId}
        confirmingDelete={confirmingDelete}
        setConfirmingDelete={setConfirmingDelete}
        setShowHistoryMobile={setShowHistoryMobile}
        openSession={openSession}
        deleteSession={deleteSession}
        groupSessionsByDate={groupSessionsByDate}
        lastUserQuestionSafe={lastUserQuestionSafe}
        getSubjectIcon={getSubjectIcon}
        showSocialMenu={showSocialMenu}
        setShowSocialMenu={setShowSocialMenu}
        socialMenuRef={socialMenuRef}
        setShowFeedback={setShowFeedback}
        newSessions={newSessions}
        openDeleteAllModal={openDeleteAllModal}
        confirmDeleteAll={confirmDeleteAll}
        closeDeleteAllModal={closeDeleteAllModal}
        DangerModal={DangerModal}
        currentUser={currentUser}
        setActiveTab={setActiveTab}
        setProfileSubTab={setProfileSubTab}
      />
    </div>
  );
}